import { MonetaryAmountSchema } from '@multiplier/lib-shared-monetary';
import { z } from 'zod';
import { BusinessContextSchema, MoneyBagReportSchema, ReportIDSchema, RowIDSchema } from './BusinessAnalysisSchemas.js';
import { EntityReportSpecificationSchema, ImportRefSchema } from './ReportSpecificationSchemas.js';
export const ReportGenerationSourceSchema = z.enum(['xero-api', 'xero-bigquery', 'xlsx']);
export const ReportGenerationSourcesSchema = z.record(ImportRefSchema, ReportGenerationSourceSchema);
function parseReportGenerationSourcesQuery(value) {
    if (typeof value !== 'string') {
        return value;
    }
    try {
        return JSON.parse(value);
    }
    catch {
        return value;
    }
}
/**
 * Zod schema for P&L report query parameters
 * Maps to the HTTP GET request query string for /api/reports/profit-and-loss
 */
export const ProfitAndLossQuerySchema = z.object({
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    startDate: z.iso.datetime().describe('Report period start date in ISO format (e.g., 2024-01-01T00:00:00Z)'),
    endDate: z.iso.datetime().describe('Report period end date in ISO format (e.g., 2024-12-31T23:59:59Z)'),
});
/**
 * Zod schema for ReportRow JSON representation
 * Used for generic report sections (P&L, Balance Sheet)
 */
export const GenericReportRowSchema = z.object({
    accountId: RowIDSchema.optional().describe('Optional unique identifier for the account'),
    title: z.string().optional().describe('Optional title for this row'),
    data: MonetaryAmountSchema.optional().describe('Optional data for this row'),
});
/**
 * Zod schema for ReportSection JSON representation
 */
export const ReportSectionSchema = z.object({
    title: z.string().describe('Section title'),
    total: MonetaryAmountSchema.optional().describe('Optional total for this section'),
    summary: GenericReportRowSchema.optional().describe('Optional summary row'),
    rows: z.array(GenericReportRowSchema),
});
export const GenericReportSchema = z.object({
    header: z
        .object({
        title: z.string().describe('Report title'),
        data: z.object({
            fromDate: z.iso.datetime().describe('Report period start date in ISO format'),
            toDate: z.iso.datetime().describe('Report period end date in ISO format'),
        }),
    })
        .optional(),
    sections: z.array(ReportSectionSchema),
});
/**
 * Zod schema for ReportPeriod JSON representation
 */
export const ReportPeriodSchema = z.object({
    fromDate: z.iso.datetime().describe('Report period start date in ISO format'),
    toDate: z.iso.datetime().describe('Report period end date in ISO format'),
});
/**
 * Zod schema for ProfitAndLossReport JSON response
 * Maps to the HTTP response body from /api/reports/profit-and-loss
 */
export const ProfitAndLossResponseSchema = z.object({
    dateRange: ReportPeriodSchema,
    currency: z.string().length(3).describe('3-letter ISO currency code (e.g., USD)'),
    revenue: ReportSectionSchema,
    costOfGoodsSold: ReportSectionSchema,
    operatingExpenses: ReportSectionSchema,
    report: z.looseObject({}).describe('The raw report from the provider'),
});
/**
 * Zod schema for balance sheet report query parameters
 * Maps to the HTTP GET request query string for /api/reports/balance-sheet
 */
export const BalanceSheetQuerySchema = z.object({
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    date: z.iso.datetime().describe('Balance sheet date in ISO format (e.g., 2025-06-01T00:00:00Z)'),
});
/**
 * Zod schema for BalanceSheetReport JSON response
 * Maps to the HTTP response body from /api/reports/balance-sheet
 */
export const BalanceSheetResponseSchema = z.object({
    dateRange: ReportPeriodSchema,
    currency: z.string().length(3).describe('3-letter ISO currency code (e.g., USD)'),
    fixedAssets: ReportSectionSchema,
    currentAssets: ReportSectionSchema,
    currentLiabilities: ReportSectionSchema,
    report: z.looseObject({}).describe('The raw report from the provider'),
});
/**
 * Zod schema for FCF report query parameters
 * Maps to the HTTP GET request query string for /api/reports/free-cash-flow
 */
export const FreeCashFlowQuerySchema = z
    .object({
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    reportId: ReportIDSchema.describe('Report specification identifier (e.g., "fcf")'),
    startDate: z.iso.datetime().describe('Report period start date in ISO format (e.g., 2024-01-01T00:00:00Z)'),
    endDate: z.iso.datetime().describe('Report period end date in ISO format (e.g., 2024-12-31T23:59:59Z)'),
    periodType: z
        .enum(['MONTH', 'YEAR'])
        .optional()
        .describe('Period type for balance sheet comparison (defaults to MONTH)'),
    sources: z
        .preprocess(parseReportGenerationSourcesQuery, ReportGenerationSourcesSchema.optional())
        .describe('Optional ImportRef-keyed report generation source selection'),
})
    .refine(({ startDate, endDate }) => new Date(startDate) <= new Date(endDate), {
    message: 'startDate must be on or before endDate',
    path: ['endDate'],
});
export const ReportGenerationSourcesQuerySchema = z.object({
    rootBusinessContext: BusinessContextSchema.describe('Root business context whose descendant source availability to list'),
});
export const ReportGenerationSourcesResponseSchema = z.record(BusinessContextSchema, z.array(ReportGenerationSourceSchema));
/**
 * Zod schema for FreeCashFlowReport JSON response
 * Maps to the HTTP response body from /api/reports/free-cash-flow
 */
export const FreeCashFlowResponseSchema = z.object({
    dateRange: ReportPeriodSchema,
    currency: z.string().length(3).describe('3-letter ISO currency code (e.g., USD)'),
    generatedAt: z.iso.datetime().describe('Report generation timestamp'),
    revenue: ReportSectionSchema,
    costOfGoodsSold: ReportSectionSchema,
    grossProfit: ReportSectionSchema,
    operationalExpenditures: ReportSectionSchema,
    ebitda: ReportSectionSchema,
    depreciation: ReportSectionSchema,
    ebit: ReportSectionSchema,
    netOperatingProfitAfterTaxes: ReportSectionSchema,
    currentLiabilities: ReportSectionSchema,
    currentAssets: ReportSectionSchema,
    changeInNetWorkingCapital: ReportSectionSchema,
    freeCashFlow: ReportSectionSchema,
    summary: ReportSectionSchema,
    reports: z.object({
        balanceSheet: GenericReportSchema,
        profitAndLoss: GenericReportSchema,
    }),
});
/**
 * Zod schema for ReportSpecification query parameters
 */
export const ReportSpecificationQuerySchema = z.object({
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
});
/**
 * Zod schema for ReportSpecification response
 * Returns an array of all EntityReportSpecifications for the business context
 */
export const ReportSpecificationResponseSchema = z.array(EntityReportSpecificationSchema);
/**
 * Zod schema for ReportBuilderSources query parameters
 * Maps to the HTTP GET request query string for /api/report-builder-sources
 */
export const ReportBuilderSourcesQuerySchema = z.object({
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    startDate: z.iso
        .datetime()
        .optional()
        .describe('Report period start date in ISO format (defaults to current year start)'),
    endDate: z.iso.datetime().optional().describe('Report period end date in ISO format (defaults to current year end)'),
});
/**
 * Zod schema for ReportBuilderSources response
 * Maps to the HTTP response body from /api/report-builder-sources
 */
export const ReportBuilderSourcesResponseSchema = z.object({
    sourceType: z.enum(['xero', 'xlsx']).optional().describe('The data source type, if configured'),
    reports: z.object({
        profitAndLoss: GenericReportSchema,
        balanceSheet: GenericReportSchema,
    }),
});
/**
 * Zod schema for FCF export request body
 * Maps to the HTTP POST request body for /api/reports/free-cash-flow/export
 */
export const FreeCashFlowExportRequestSchema = z.object({
    report: MoneyBagReportSchema.describe('The generated multicurrency FCF report to export'),
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    periodType: z.enum(['MONTH', 'YEAR']).describe('Period type for the report'),
});
//# sourceMappingURL=ReportSchemas.js.map